<template>
  <div class="flex flex-col dable-grid" style="min-height: calc(100vh - 200px)">
    <product-selection-bar
      v-if="Object.keys(selectedRows).length > 0"
      :selected-count="Object.keys(selectedRows).length"
      :only-selected="onlySelected"
      :edit-mode="editMode"
      :remove-current-offer="removeCurrentOffer"
      :entity="entity"
      :editable-headers="headers.filter((h) => h.editable)"
      :hidden_columns="hidden_columns"
      :label-export-loading="labelExportLoading"
      :resync-loading="resyncFinancaLoading"
      :save-loading="saveLoading"
      @clear-selection="clearSelection"
      @update:onlySelected="onOnlySelectedChange"
      @update:editMode="onEditModeChange"
      @update:removeCurrentOffer="removeCurrentOffer = $event"
      @apply="applyChanges"
      @save="saveChanges"
      @restore="restoreDefaultPrices"
      @export-csv="exportSelected"
      @export-labels="exportSelectedLabels"
      @resync-financa="resyncSelectedFinanca"
    />
    <LabelExportModal
      :open="showLabelModal"
      :loading="labelExportLoading"
      :export-context="labelExportContext"
      :mode="labelModalMode"
      :notice-product-count="labelNoticeCount"
      title="Eksporto etiketat (PDF)"
      @close="closeLabelModal"
      @confirm="confirmLabelExport"
    />

    <div class="dable-grid__scroll-bar">
      <button type="button" class="dable-scroll-btn" title="Lëviz majtas" :disabled="!canScrollLeft" @click="scrollTableBy(-360)">
        <i class="fa fa-chevron-left"></i>
      </button>
      <span class="dable-scroll-hint">Shift + rrota e mouse për të lëvizur majtas/djathtas</span>
      <button type="button" class="dable-scroll-btn" title="Lëviz djathtas" :disabled="!canScrollRight" @click="scrollTableBy(360)">
        <i class="fa fa-chevron-right"></i>
      </button>
    </div>

    <div ref="tableWrap" class="dable-grid__table-wrap" @scroll="updateScrollState">
      <table class="dable-table">
        <thead class="select-none dable-table__head">
          <tr class="dable-table__header-row">
            <th class="dable-table__checkbox-col dable-table__checkbox-col--head" rowspan="2" title="Zgjidh produktet">
              <label class="dable-checkbox-label dable-checkbox-label--header">
                <input type="checkbox" class="dable-checkbox" :checked="allPageRowsSelected" :indeterminate.prop="somePageRowsSelected && !allPageRowsSelected" @change="selectAllPage" />
              </label>
            </th>
            <th
              v-for="(header, index) in visibleHeaders"
              :key="'h-' + header.field"
              class="dable-table__th"
              :data-col-field="header.field"
              :style="getColumnStyle(header)"
              @dragover.prevent="onDragOver($event)"
              @dragleave="onDragLeave($event)"
              @drop.prevent="onDrop($event, index)"
            >
              <div class="dable-table__th-inner" @contextmenu.prevent="groupBy(header.field)">
                <span v-if="canDragColumn(header)" class="dable-table__drag-handle" draggable="true" title="Tërhiq për të ndryshuar rendin" @dragstart="onDragStart($event, index)" @dragend="onDragEnd">
                  <i class="fa fa-grip-vertical"></i>
                </span>
                <span v-if="header.field === 'actions'" class="dable-table__th-label" title="Veprime"><i class="fa fa-pencil"></i></span>
                <button v-else type="button" class="dable-table__th-label" @click="sortBy(header.field)">
                  {{ header.headerName }}
                  <span v-if="currentSortField === getSortKey(header)" class="dable-table__sort-icon">
                    {{ currentSortDir === "asc" ? "▲" : "▼" }}
                  </span>
                </button>
                <span v-if="groupedBy?.includes(header.field)" class="dable-table__group-icon"><i class="fa fa-object-group"></i></span>
                <div class="resizer" @mousedown.prevent="startResizing($event, header.field)"></div>
              </div>
            </th>
          </tr>
          <tr class="dable-table__filter-row">
            <th v-for="header in visibleHeaders" :key="'f-' + header.field" class="dable-table__filter-cell" :data-col-field="header.field" :style="getColumnStyle(header)">
              <template v-if="header.filter && header.field !== 'actions'">
                <dable-multiselect-filter
                  v-if="isMultiselectFilter(header)"
                  :options="getFilterOptions(header)"
                  :model-value="getFilterListValue(header)"
                  @update:modelValue="onFilterInput(header, $event)"
                />
                <select v-else-if="isSelectFilter(header)" class="dable-filter-input" :value="getFilterValue(header)" @change="onFilterInput(header, $event.target.value)">
                  <option value="">Të gjitha</option>
                  <option v-for="opt in getFilterOptions(header)" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
                </select>
                <input
                  v-else
                  class="dable-filter-input"
                  type="text"
                  :value="getFilterValue(header)"
                  :placeholder="header.field === 'code' ? 'Kodi + Enter' : header.headerName"
                  @input="onFilterInput(header, $event.target.value)"
                  @keydown.enter.prevent="onFilterEnter(header, $event.target.value)"
                />
              </template>
            </th>
          </tr>
        </thead>
        <tbody>
          <template v-for="row in rows" :key="row.id">
            <tr v-if="row.isGroup" class="dable-table__group-row">
              <td></td>
              <td :colspan="visibleHeaders.length">
                <button type="button" @click="toggleGroup(row.groupKey)">
                  <span v-if="row.collapsed">[+]</span>
                  <span v-else>[-]</span>
                </button>
                <template v-for="(groupName, gi) in row.groupNames" :key="gi">
                  <strong>{{ groupName.value }}</strong
                  ><br />
                </template>
              </td>
            </tr>
            <tr v-else class="dable-table__row" :class="{ 'dable-table__row--inactive': entity === 'product' && row.columns.status == 0 }">
              <td class="dable-table__checkbox-col">
                <label class="dable-checkbox-label dable-checkbox-label--row">
                  <input type="checkbox" class="dable-checkbox" :checked="!!selectedRows['row-' + row.id]" @change="select(row)" />
                </label>
              </td>
              <td v-for="header in visibleHeaders" :key="header.field + '-' + row.id" class="dable-table__td" :data-col-field="header.field" :style="getColumnStyle(header)">
                <div class="dable-table__cell" :title="getCellTitle(row, header)">
                  <div v-if="typeof header.cellRenderer === 'function' && editMode == false" v-html="header.cellRenderer({ data: row.cells })"></div>
                  <div v-else-if="typeof header.cellRenderer === 'function' && editMode == true">
                    <input
                      v-if="header.editable && selectedRows['row-' + row.id]"
                      v-model="selectedRows['row-' + row.id].cells[header.field]"
                      class="input is-small"
                      :type="header.editable.type"
                      :class="{ 'is-edited': isCellEdited(row, header) }"
                    />
                    <div v-else class="overflow-hidden border-gray-100 input is-small">
                      <div class="truncate" v-html="row.cells[header.field]" :class="[row.columns[header.field] == '[Pa Percaktuar]' ? 'text-gray-200' : '']"></div>
                    </div>
                  </div>
                  <div v-else-if="header.is_custom_component && !editMode">
                    <component :is="resolveComponent(header.custom_component)" :data="row.columns"></component>
                  </div>
                  <div v-else-if="typeof header.cellRenderer !== 'function'">
                    <div v-if="!editMode || !selectedRows['row-' + row.id]" :class="[row.columns[header.field] == '[Pa Percaktuar]' ? 'text-gray-200' : '']">
                      {{ row.cells[header.field] }}
                    </div>
                    <div v-else-if="header.editable && header.editable.type != 'select'">
                      <input v-model="selectedRows['row-' + row.id].cells[header.field]" class="input is-small" :type="header.editable.type" />
                    </div>
                    <div
                      v-else
                      class="overflow-hidden border-gray-100 input is-small"
                      :class="{ 'is-edited': isCellEdited(row, header) }"
                    >
                      <div class="truncate">{{ row.cells[header.field] }}</div>
                    </div>
                  </div>
                </div>
              </td>
            </tr>
          </template>
          <tr v-if="rows.length === 0">
            <td :colspan="visibleHeaders.length + 1" class="dable-table__empty">Nuk u gjet asnjë rekord.</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="onlySelected == false" class="dable-pagination">
      <div class="dable-pagination__pages">
        <button v-for="page in visiblePages" :key="String(page)" type="button" class="dable-pagination__btn" :class="{ 'dable-pagination__btn--active': currentPage === page }" @click="handlePageClick(page)">
          {{ typeof page === "object" ? page.label : page }}
        </button>
      </div>
      <small class="dable-pagination__total">{{ formatNumbers(serverMode ? totalRows : sortedData.length) }} rekorde</small>
    </div>
  </div>
</template>

<script>
import { ref, computed, markRaw } from "vue";
import numeral from "numeral";
import ProductsServices from "@/services/Product";
import { financaBulkFailureText } from "@/utils/financaSaveNotice";
import LabelExportModal from "@/components/LabelExportModal.vue";
import ProductSelectionBar from "@/pages/products/components/product-selection-bar.vue";
import DableMultiselectFilter from "@/components/dable-multiselect-filter.vue";

const BULK_UNCHANGED = "__mos_ndrysho__";

export default {
  components: { LabelExportModal, ProductSelectionBar, DableMultiselectFilter },
  props: {
    headers: { type: Array, required: true },
    data: { type: Array, required: true },
    currPage: { type: Number, default: 1 },
    entity: { type: String, default: "" },
    hidden_columns: { type: Array, default: () => [] },
    serverMode: { type: Boolean, default: false },
    totalRows: { type: Number, default: 0 },
    pageSize: { type: Number, default: 50 },
    serverFilters: { type: Object, default: () => ({}) },
    sortField: { type: String, default: "name" },
    sortDir: { type: String, default: "asc" },
    loading: { type: Boolean, default: false },
    saveLoading: { type: Boolean, default: false },
    layoutStorageKey: { type: String, default: "" },
  },
  emits: ["save-changes", "page-change", "query-change", "pin-code"],
  data() {
    return {
      filterDebounceTimer: null,
      canScrollLeft: false,
      canScrollRight: false,
    };
  },
  watch: {
    headers: {
      handler(val) {
        this.mergeHeadersFromProps(val);
        this.$nextTick(() => this.updateScrollState());
      },
      deep: true,
    },
    data() {
      this.$nextTick(() => this.updateScrollState());
    },
    currPage(val) {
      this.currentPage = val;
    },
    pageSize(val) {
      this.itemsPerPage = val;
    },
  },
  computed: {
    labelExportContext() {
      const guids = Object.keys(this.selectedRows || {})
        .map((key) => this.selectedRows[key]?.cells?.guid)
        .filter(Boolean);
      return {
        product_guids: guids,
        category_guid: null,
        related_entity: "products",
        include_descendants: false,
      };
    },
  },
  methods: {
    onTableWheel(event) {
      const el = this.$refs.tableWrap;
      if (!el) return;

      // Shift + vertical wheel → horizontal scroll (mice without a tilt wheel).
      if (event.shiftKey && Math.abs(event.deltaY) > Math.abs(event.deltaX)) {
        if (el.scrollWidth <= el.clientWidth) return;
        event.preventDefault();
        el.scrollLeft += event.deltaY;
        this.updateScrollState();
      }
    },
    scrollTableBy(delta) {
      const el = this.$refs.tableWrap;
      if (!el) return;
      el.scrollBy({ left: delta, behavior: "smooth" });
      this.$nextTick(() => this.updateScrollState());
      setTimeout(() => this.updateScrollState(), 350);
    },
    updateScrollState() {
      const el = this.$refs.tableWrap;
      if (!el) {
        this.canScrollLeft = false;
        this.canScrollRight = false;
        return;
      }
      const max = el.scrollWidth - el.clientWidth;
      this.canScrollLeft = el.scrollLeft > 2;
      this.canScrollRight = max > 2 && el.scrollLeft < max - 2;
    },
    getSortKey(header) {
      return header.sortField || header.filterField || header.field;
    },
    getFilterKey(header) {
      return header.filterField || header.field;
    },
    getLiveHeader(header) {
      return this.headers.find((h) => h.field === header?.field) || header;
    },
    isSelectFilter(header) {
      const live = this.getLiveHeader(header);
      if (live.filterType === "multiselect") return false;
      if (live.filterType === "select") return true;
      if (live.filterType === "text") return false;
      return live.floatingFilter === false;
    },
    isMultiselectFilter(header) {
      return this.getLiveHeader(header).filterType === "multiselect";
    },
    getFilterOptions(header) {
      const live = this.getLiveHeader(header);
      if (live.filterOptions?.length) return live.filterOptions;
      if (header.filterOptions?.length) return header.filterOptions;
      if (header.field === "is_accessory") {
        return [
          { value: "1", label: "Po" },
          { value: "0", label: "Jo" },
        ];
      }
      return [];
    },
    getFilterValue(header) {
      const key = this.getFilterKey(header);
      return this.serverMode ? this.serverFilters[key] ?? "" : this.localFilters[key] ?? "";
    },
    getFilterListValue(header) {
      const value = this.getFilterValue(header);
      if (Array.isArray(value)) return value;
      if (value == null || value === "") return [];
      return [value];
    },
    onFilterEnter(header, value) {
      const key = this.getFilterKey(header);
      const trimmed = String(value ?? "").trim();
      if (!trimmed) return;
      if (key === "code") {
        this.$emit("pin-code", trimmed);
        if (this.serverMode) {
          const next = { ...this.serverFilters };
          delete next.code;
          this.emitQueryChange({ filters: next, page: 1, immediate: true });
        } else {
          delete this.localFilters.code;
        }
        return;
      }
      if (this.serverMode) {
        const next = { ...this.serverFilters, [key]: trimmed };
        this.emitQueryChange({ filters: next, page: 1, immediate: true });
      }
    },
    onFilterInput(header, value) {
      const key = this.getFilterKey(header);
      const live = this.getLiveHeader(header);
      const empty = value === "" || value == null || (Array.isArray(value) && value.length === 0);
      const immediate = Array.isArray(value) || live.filterType === "multiselect";
      if (this.serverMode) {
        const next = { ...this.serverFilters };
        if (empty) delete next[key];
        else next[key] = value;
        this.emitQueryChange({ filters: next, page: 1, immediate });
        return;
      }
      if (empty) delete this.localFilters[key];
      else this.localFilters[key] = value;
      this.currentPage = 1;
    },
    emitQueryChange(partial = {}) {
      if (this.filterDebounceTimer) clearTimeout(this.filterDebounceTimer);
      this.filterDebounceTimer = setTimeout(
        () => {
          this.$emit("query-change", {
            filters: partial.filters ?? this.serverFilters,
            sortField: partial.sortField ?? this.currentSortField,
            sortDir: partial.sortDir ?? this.currentSortDir,
            page: partial.page ?? this.currentPage,
          });
        },
        partial.immediate ? 0 : 300,
      );
    },
    getCellTitle(row, header) {
      const val = row.cells[header.field];
      if (val == null || typeof val === "object") return "";
      return String(val);
    },
    clearSelection() {
      this.selectedRows = {};
      this.onlySelected = false;
      this.editMode = false;
      this.removeCurrentOffer = false;
    },
    /**
     * Select loaded grid rows by product guid and enable bulk-edit mode.
     * @param {string[]} guids
     * @returns {number} how many rows were selected
     */
    selectByGuids(guids = []) {
      const wanted = new Set(
        (Array.isArray(guids) ? guids : [])
          .map((g) => String(g || "").trim())
          .filter(Boolean)
      );
      if (!wanted.size) {
        this.clearSelection();
        return 0;
      }

      const next = {};
      const sourceRows = (this.rows || []).filter((row) => row && !row.isGroup);
      sourceRows.forEach((row) => {
        const guid = row.cells?.guid || row.columns?.guid;
        if (guid && wanted.has(String(guid))) {
          next["row-" + row.id] = row;
        }
      });

      // Fallback: build rows from raw data if computed rows are empty/stale
      if (Object.keys(next).length === 0 && Array.isArray(this.data)) {
        this.data.forEach((item) => {
          const guid = item?.guid;
          if (guid && wanted.has(String(guid)) && item.id != null) {
            next["row-" + item.id] = {
              id: item.id,
              columns: item,
              cells: { ...item },
            };
          }
        });
      }

      this.selectedRows = next;
      const count = Object.keys(next).length;
      if (count > 0) {
        this.onlySelected = true;
        this.editMode = true;
        this.currentPage = 1;
      } else {
        this.onlySelected = false;
        this.editMode = false;
      }
      return count;
    },
    onOnlySelectedChange(val) {
      this.onlySelected = val;
      this.currentPage = 1;
    },
    onEditModeChange(val) {
      this.editMode = val;
      if (!val) {
        this.removeCurrentOffer = false;
        return;
      }
      if (Object.keys(this.selectedRows).length > 0) {
        this.onlySelected = true;
        this.currentPage = 1;
      }
    },
    isCellEdited(row, header) {
      if (!header?.editable) return false;
      const selected = this.selectedRows["row-" + row.id];
      if (!selected?.cells) return false;
      const original = (this.data || []).find((d) => d.id == row.id);
      return original?.[header.field] != selected.cells[header.field];
    },
    isBulkUnchanged(value) {
      return value === undefined || value === BULK_UNCHANGED;
    },
    getSelectedProductRows() {
      return Object.values(this.selectedRows || {}).filter((row) => row && !row.isGroup);
    },
    syncVisibleRowCells() {
      this.rows.forEach((row) => {
        if (row.isGroup) return;
        const selected = this.selectedRows["row-" + row.id];
        if (selected && selected !== row && selected.cells) {
          Object.assign(row.cells, selected.cells);
        }
      });
    },
    applyRemoveCurrentOffer(row) {
      const base = row.columns?.default_price ?? row.cells?.default_price ?? row.columns?.price ?? row.cells?.price;
      row.cells.price = base;
      row.cells.has_offer_name = "Jo";
      if (row.cells.has_offer != null) row.cells.has_offer = 0;
    },
    restoreDefaultPrices() {
      this.getSelectedProductRows().forEach((row) => {
        this.headers.forEach((h) => {
          if (!h.editable) return;
          const exists = row.columns?.["default_" + h.field];
          if (exists != null && exists !== "") {
            row.cells[h.field] = exists;
            if (h.field === "price") {
              row.cells.has_offer_name = "Jo";
              if (row.cells.has_offer != null) row.cells.has_offer = 0;
            }
          }
        });
      });
      this.syncVisibleRowCells();
    },
    applyChanges() {
      this.getSelectedProductRows().forEach((row) => {
        this.headers.forEach((h) => {
          if (!h.editable || this.hidden_columns.includes(h.field)) return;
          if (this.isBulkUnchanged(h.editable.value)) return;
          if (h.editable.type == "number") {
            if (h.editable.value === "" || h.editable.value == null) return;
            if (h.editable.modules) {
              if (h.editable.field == "price") {
                const amount = h.editable.operation_mode == "percentage" ? parseFloat(h.editable.value) : parseInt(h.editable.value, 10);
                if (!amount) return;
                const current = Number(row.cells[h.field]) || 0;
                if (h.editable.operation_mode == "number") {
                  if (h.editable.operation_value == "add") row.cells[h.field] = current + amount;
                  else if (h.editable.operation_value == "sub") row.cells[h.field] = current - amount;
                } else if (h.editable.operation_mode == "percentage") {
                  if (h.editable.operation_value == "add") row.cells[h.field] = current + (amount / 100) * current;
                  else if (h.editable.operation_value == "sub") row.cells[h.field] = current - (amount / 100) * current;
                }
              } else row.cells[h.field] = parseInt(h.editable.value, 10);
            } else row.cells[h.field] = parseInt(h.editable.value, 10);
          } else if (h.editable.type == "select") {
            row.cells[h.field] = h.editable.options.find((o) => o.id == h.editable.value)?.label ?? h.editable.empty?.label;
            row.cells[h.editable.field] = h.editable.value;
          } else {
            row.cells[h.field] = h.editable.value;
          }
        });
        if (this.removeCurrentOffer) this.applyRemoveCurrentOffer(row);
      });
      this.syncVisibleRowCells();
    },
    saveChanges() {
      let changes = this.collectChanges();
      if (Object.keys(changes).length === 0) {
        this.applyChanges();
        changes = this.collectChanges();
      }
      this.$emit("save-changes", changes);
    },
    collectChanges() {
      const changes = {};
      this.getSelectedProductRows().forEach((row) => {
        if (!row.cells || Object.keys(row.cells).length === 0) return;
        Object.keys(row.cells).forEach((cell) => {
          if (this.hidden_columns.includes(cell) && cell !== "price") return;
          if (!row.columns || !(cell in row.columns)) return;
          if (row.cells[cell] == row.columns[cell]) return;
          const header = this.headers.find((h) => h.field == cell && h.editable);
          if (!header) return;
          let c = cell;
          if (header.editable.type == "select") c = header.editable.field;
          if (c === "has_offer" || c === "has_offer_name") return;
          if (!changes[row.id]) changes[row.id] = {};
          changes[row.id][c] = row.cells[c];
        });
      });
      return changes;
    },
    select(row) {
      if (this.selectedRows["row-" + row.id]) delete this.selectedRows["row-" + row.id];
      else this.selectedRows["row-" + row.id] = row;
    },
    selectAllPage() {
      const pageRows = this.rows.filter((r) => !r.isGroup);
      const allSelected = pageRows.every((row) => this.selectedRows["row-" + row.id]);
      pageRows.forEach((row) => {
        if (allSelected) delete this.selectedRows["row-" + row.id];
        else if (!this.selectedRows["row-" + row.id]) this.selectedRows["row-" + row.id] = row;
      });
      if (Object.keys(this.selectedRows).length === 0) this.onlySelected = false;
    },
    formatNumbers(number) {
      return numeral(number).format("0,0");
    },
    resolveComponent(componentName) {
      return this.customComponents[componentName];
    },
    handlePageClick(page) {
      if (typeof page === "object") {
        if (page.direction === "forward") this.currentPage = Math.min(this.currentPage + 3, this.totalPages);
        else this.currentPage = Math.max(this.currentPage - 3, 1);
      } else {
        this.currentPage = page;
      }
      if (this.serverMode) {
        this.$emit("page-change", this.currentPage);
      }
    },
    sortBy(key) {
      const header = this.headers.find((h) => h.field === key);
      const sortKey = header ? this.getSortKey(header) : key;
      let dir = "asc";
      if (this.currentSortField === sortKey) {
        dir = this.currentSortDir === "asc" ? "desc" : "asc";
      }
      if (this.serverMode) {
        this.$emit("query-change", {
          filters: this.serverFilters,
          sortField: sortKey,
          sortDir: dir,
          page: 1,
          immediate: true,
        });
        return;
      }
      if (this.sortKey === key) this.sortOrders[key] = this.sortOrders[key] * -1;
      else {
        this.sortKey = key;
        this.sortOrders[key] = 1;
      }
    },
    startResizing(event, field) {
      event.stopPropagation();
      const header = this.tableheaders.find((h) => h.field === field);
      if (!header) return;
      const th = event.target.closest("th");
      this.resizing = true;
      this.resizingField = field;
      this.resizeStartX = event.clientX;
      this.resizeStartWidth = th ? th.getBoundingClientRect().width : header.width || 100;
      document.addEventListener("mousemove", this.handleMouseMove);
      document.addEventListener("mouseup", this.stopResizing);
    },
    handleMouseMove(event) {
      if (!this.resizingField) return;
      const header = this.tableheaders.find((h) => h.field === this.resizingField);
      if (!header) return;
      const minW = header.minWidth ?? (header.field === "actions" ? 56 : 60);
      const delta = event.clientX - this.resizeStartX;
      header.width = Math.max(minW, Math.round(this.resizeStartWidth + delta));
      this.tableheaders = [...this.tableheaders];
      document.body.classList.add("resizing");
    },
    stopResizing() {
      if (this.resizingField && this.layoutStorageKey) {
        this.saveColumnWidths();
      }
      this.resizingField = null;
      this.resizing = false;
      document.removeEventListener("mousemove", this.handleMouseMove);
      document.removeEventListener("mouseup", this.stopResizing);
      document.body.classList.remove("resizing");
    },
    canDragColumn(header) {
      return header?.field && header.field !== "actions";
    },
    getDefaultColumnWidth(header) {
      const field = header?.field;
      const byField = {
        actions: 64,
        is_accessory: 56,
        code: 130,
        name: 200,
        type_id_name: 240,
        description: 180,
        known_as: 180,
        price: 90,
        vat: 90,
        brand_name: 110,
        unit_name: 110,
        status: 80,
        in_web_name: 80,
        in_store_name: 110,
        fis_status: 80,
        barcode: 140,
        created_at: 120,
        updated_at: 120,
        manager_name: 130,
        origin_country: 100,
        warranty_name: 110,
        packaging: 100,
        warranty_airbag: 100,
        created_by_name: 120,
        updated_by_name: 120,
      };
      if (field && byField[field] != null) return byField[field];
      if (field && /^[0-9a-f-]{36}$/i.test(field)) return 100;
      const label = header?.headerName || field || "";
      return Math.max(90, label.length * 8 + 24);
    },
    getColumnStyle(header) {
      const minW = header.minWidth ?? (header.field === "actions" ? 56 : 60);
      let width = header.width;
      if (width != null) {
        width = typeof width === "number" ? width : parseInt(width, 10);
        if (Number.isNaN(width)) width = null;
      }
      if (width == null) width = this.getDefaultColumnWidth(header);
      return {
        width: `${width}px`,
        minWidth: `${minW}px`,
      };
    },
    getLayoutStorageKeys() {
      if (!this.layoutStorageKey) return null;
      return {
        order: `dable_layout_${this.layoutStorageKey}_order`,
        widths: `dable_layout_${this.layoutStorageKey}_widths`,
      };
    },
    loadSavedOrder() {
      const keys = this.getLayoutStorageKeys();
      if (!keys) return null;
      try {
        const raw = localStorage.getItem(keys.order);
        return raw ? JSON.parse(raw) : null;
      } catch {
        return null;
      }
    },
    loadSavedWidths() {
      const keys = this.getLayoutStorageKeys();
      if (!keys) return null;
      try {
        const raw = localStorage.getItem(keys.widths);
        return raw ? JSON.parse(raw) : null;
      } catch {
        return null;
      }
    },
    saveColumnOrder() {
      const keys = this.getLayoutStorageKeys();
      if (!keys) return;
      localStorage.setItem(keys.order, JSON.stringify(this.tableheaders.map((h) => h.field)));
    },
    saveColumnWidths() {
      const keys = this.getLayoutStorageKeys();
      if (!keys) return;
      const widths = {};
      this.tableheaders.forEach((h) => {
        if (h.width) {
          const w = typeof h.width === "number" ? h.width : parseInt(h.width, 10);
          if (!Number.isNaN(w)) widths[h.field] = w;
        }
      });
      localStorage.setItem(keys.widths, JSON.stringify(widths));
    },
    ensureActionsFirst(headers) {
      const idx = headers.findIndex((h) => h.field === "actions");
      if (idx > 0) {
        const [actionsCol] = headers.splice(idx, 1);
        headers.unshift(actionsCol);
      }
    },
    mergeHeadersFromProps(newHeaders) {
      const savedOrder = this.loadSavedOrder();
      const savedWidths = this.loadSavedWidths();
      const byField = new Map((newHeaders || []).map((h) => [h.field, { ...h }]));
      const merged = [];

      if (Array.isArray(savedOrder) && savedOrder.length) {
        savedOrder.forEach((field) => {
          const header = byField.get(field);
          if (header) {
            if (savedWidths?.[field]) header.width = savedWidths[field];
            merged.push(header);
            byField.delete(field);
          }
        });
      }

      byField.forEach((header) => {
        if (savedWidths?.[header.field]) header.width = savedWidths[header.field];
        merged.push(header);
      });

      this.ensureActionsFirst(merged);
      merged.forEach((header) => {
        if (header.editable?.type === "select" && (header.editable.value === undefined || header.editable.value === null)) {
          header.editable.value = BULK_UNCHANGED;
        }
      });
      this.tableheaders = merged;
    },
    onDragStart(event, visibleIndex) {
      const header = this.visibleHeaders[visibleIndex];
      if (!header || !this.canDragColumn(header)) {
        event.preventDefault();
        return;
      }
      event.dataTransfer.setData("text/plain", header.field);
      event.dataTransfer.effectAllowed = "move";
      event.target.closest("th")?.classList.add("dragging");
    },
    onDragEnd() {
      document.querySelector(".dragging")?.classList.remove("dragging");
      document.querySelectorAll(".hover-to-drop").forEach((el) => el.classList.remove("hover-to-drop"));
    },
    onDragOver(event) {
      const th = event.target.closest("th");
      if (!th) return;
      const field = th.getAttribute("data-col-field");
      if (field === "actions") return;
      th.classList.add("hover-to-drop");
    },
    onDragLeave(event) {
      event.target.closest("th")?.classList.remove("hover-to-drop");
    },
    onDrop(event, targetVisibleIndex) {
      const draggedField = event.dataTransfer.getData("text/plain");
      const targetHeader = this.visibleHeaders[targetVisibleIndex];
      if (!draggedField || !targetHeader || draggedField === targetHeader.field) return;
      if (draggedField === "actions" || targetHeader.field === "actions") return;

      const headers = [...this.tableheaders];
      const fromIdx = headers.findIndex((h) => h.field === draggedField);
      const toIdx = headers.findIndex((h) => h.field === targetHeader.field);
      if (fromIdx < 0 || toIdx < 0) return;

      const [moved] = headers.splice(fromIdx, 1);
      headers.splice(toIdx, 0, moved);
      this.ensureActionsFirst(headers);
      this.tableheaders = headers;
      if (this.layoutStorageKey) this.saveColumnOrder();
      document.querySelectorAll(".hover-to-drop").forEach((el) => el.classList.remove("hover-to-drop"));
    },
    exportSelected() {
      const visibleHeaders = this.tableheaders.filter((h) => h.hide !== true);
      const selectedData = Object.keys(this.selectedRows).map((key) => this.selectedRows[key].cells);
      const headerRow = visibleHeaders.map((h) => h.headerName).join(",");
      const dataRows = selectedData.map((row) => visibleHeaders.map((h) => `"${String(row[h.field] ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
      const link = document.createElement("a");
      link.setAttribute("href", encodeURI("data:text/csv;charset=utf-8," + headerRow + "\n" + dataRows));
      link.setAttribute("download", `export_produktet_${new Date().toISOString().split("T")[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    exportSelectedLabels() {
      const guids = Object.keys(this.selectedRows)
        .map((key) => this.selectedRows[key]?.cells?.guid)
        .filter(Boolean);
      if (guids.length === 0) return;

      this.labelExportLoading = true;
      ProductsServices.previewLabelExport({
        product_guids: guids,
        related_entity: "products",
        include_descendants: false,
      })
        .then((data) => {
          if (!data?.total_labels) {
            this.labelModalMode = "notice";
            this.labelNoticeCount = guids.length;
            this.showLabelModal = true;
            return;
          }
          this.labelModalMode = "export";
          this.labelNoticeCount = guids.length;
          this.showLabelModal = true;
        })
        .catch((e) => {
          const msg = e?.response?.data?.message || e?.message || "Gabim gjatë ngarkimit të preview.";
          if (this.$toast) this.$toast.error(msg);
          else alert(msg);
        })
        .finally(() => {
          this.labelExportLoading = false;
        });
    },
    closeLabelModal() {
      if (this.labelExportLoading) return;
      this.showLabelModal = false;
      this.labelModalMode = "export";
    },
    async confirmLabelExport({ per_page }) {
      const guids = Object.keys(this.selectedRows)
        .map((key) => this.selectedRows[key]?.cells?.guid)
        .filter(Boolean);
      if (guids.length === 0) return;
      this.labelExportLoading = true;
      try {
        await ProductsServices.exportLabels({ product_guids: guids, per_page });
        if (this.$toast) this.$toast.success("PDF i etiketave u shkarkua.");
        this.showLabelModal = false;
      } catch (e) {
        if (this.$toast) this.$toast.error(e?.message || "Nuk u shkarkua PDF i etiketave.");
        else alert(e?.message || "Nuk u shkarkua PDF i etiketave.");
      } finally {
        this.labelExportLoading = false;
      }
    },
    async resyncSelectedFinanca() {
      const ids = Object.keys(this.selectedRows)
        .map((key) => Number(this.selectedRows[key]?.cells?.id || this.selectedRows[key]?.id))
        .filter(Boolean);
      if (!ids.length) {
        if (this.$toast) this.$toast.error("Zgjidhni të paktën një produkt.");
        return;
      }
      this.resyncFinancaLoading = true;
      const chunkSize = 15;
      let syncedOk = 0;
      let syncedFailed = 0;
      const failedSync = [];
      try {
        for (let i = 0; i < ids.length; i += chunkSize) {
          const chunk = ids.slice(i, i + chunkSize);
          const res = await ProductsServices.resyncFinanca({ product_ids: chunk, source: "product_grid" });
          syncedOk += Number(res?.synced_ok || 0);
          syncedFailed += Number(res?.synced_failed || 0);
          if (Array.isArray(res?.failed_sync)) {
            failedSync.push(...res.failed_sync);
          }
        }
        if (syncedFailed > 0) {
          const detail = financaBulkFailureText(failedSync);
          if (this.$toast) this.$toast.error(detail || `F5: ${syncedOk} OK, ${syncedFailed} dështime.`);
        } else {
          if (this.$toast) this.$toast.success(`U ruajtën ${syncedOk} produkte në Financa 1 dhe Financa 2.`);
        }
      } catch (e) {
        if (this.$toast) this.$toast.error(e?.response?.data?.message || e?.message || "Nuk u sinkronizua në Financa.");
      } finally {
        this.resyncFinancaLoading = false;
      }
    },
    groupBy(key) {
      const index = this.groupedBy.indexOf(key);
      if (index === -1) this.groupedBy.push(key);
      else this.groupedBy.splice(index, 1);
    },
    toggleGroup(groupKey) {
      this.collapsedGroups[groupKey] = !this.collapsedGroups[groupKey];
    },
  },
  mounted() {
    this.mergeHeadersFromProps(this.headers);
    this.$nextTick(() => {
      this.updateScrollState();
      const el = this.$refs.tableWrap;
      if (el) {
        this._onTableWheel = (event) => this.onTableWheel(event);
        el.addEventListener("wheel", this._onTableWheel, { passive: false });
      }
    });
    this._onResizeScroll = () => this.updateScrollState();
    window.addEventListener("resize", this._onResizeScroll);
  },
  beforeUnmount() {
    window.removeEventListener("resize", this._onResizeScroll);
    const el = this.$refs.tableWrap;
    if (el && this._onTableWheel) {
      el.removeEventListener("wheel", this._onTableWheel);
    }
  },
  setup(props) {
    const customComponents = ref({});
    const currentPage = ref(props.currPage || 1);
    const selectedRows = ref({});
    const labelExportLoading = ref(false);
    const resyncFinancaLoading = ref(false);
    const showLabelModal = ref(false);
    const labelModalMode = ref("export");
    const labelNoticeCount = ref(1);
    const onlySelected = ref(false);
    const editMode = ref(false);
    const removeCurrentOffer = ref(false);
    const localFilters = ref({});
    const itemsPerPage = ref(props.serverMode ? props.pageSize : 35);
    const collapsedGroups = ref({});
    const groupedBy = ref([]);
    const resizing = ref(false);
    const resizingField = ref(null);
    const resizeStartX = ref(0);
    const resizeStartWidth = ref(0);
    const tableheaders = ref([]);
    const sortKey = ref(null);
    const sortOrders = ref(
      props.headers.reduce((orders, header) => {
        orders[header.field] = 1;
        return orders;
      }, {}),
    );

    props.headers.forEach((header) => {
      if (header.custom_component) {
        import(`@/components/dables/${header.custom_component}`).then((module) => {
          customComponents.value[header.custom_component] = markRaw(module.default);
        });
      }
    });

    const visibleHeaders = computed(() => tableheaders.value.filter((h) => h.hide != true));

    const currentSortField = computed(() => (props.serverMode ? props.sortField : sortKey.value));
    const currentSortDir = computed(() => (props.serverMode ? props.sortDir : sortKey.value ? (sortOrders.value[sortKey.value] > 0 ? "asc" : "desc") : "asc"));

    const sortedData = computed(() => {
      let filtered = [
        ...(onlySelected.value && Object.keys(selectedRows.value).length > 0
          ? Object.values(selectedRows.value)
              .filter((row) => row && !row.isGroup && row.cells)
              .map((row) => ({ ...row.cells, id: row.id }))
          : props.data ?? []),
      ];

      if (!props.serverMode) {
        Object.entries(localFilters.value).forEach(([key, value]) => {
          if (!value) return;
          const header = props.headers.find((h) => (h.filterField || h.field) === key);
          const isSelect = header && (header.filterType === "select" || header.floatingFilter === false);
          filtered = filtered.filter((item) => {
            const cell = item[key] ?? item[header?.field];
            if (cell == null) return false;
            return isSelect ? String(cell).toLowerCase() === String(value).toLowerCase() : String(cell).toLowerCase().includes(String(value).toLowerCase());
          });
        });
        if (sortKey.value) {
          const order = sortOrders.value[sortKey.value] || 1;
          filtered = [...filtered].sort((a, b) => {
            const av = a[sortKey.value] ?? "";
            const bv = b[sortKey.value] ?? "";
            return (av === bv ? 0 : av > bv ? 1 : -1) * order;
          });
        }
      }

      return filtered;
    });

    const groupedRows = computed(() => {
      if (groupedBy.value.length === 0) {
        return sortedData.value.map((item) => ({ id: item.id, columns: item, cells: { ...item } }));
      }
      const groups = sortedData.value.reduce((acc, item) => {
        const key = groupedBy.value.map((g) => item[g]).join(" | ");
        if (!acc[key]) acc[key] = [];
        acc[key].push(item);
        return acc;
      }, {});
      const result = [];
      Object.entries(groups).forEach(([group, items]) => {
        const groupParts = group.split(" | ");
        result.push({
          isGroup: true,
          groupNames: groupedBy.value.map((g, i) => ({ key: g, value: groupParts[i] })),
          collapsed: collapsedGroups.value[group],
          groupKey: group,
          id: "group-" + group,
        });
        if (!collapsedGroups.value[group]) {
          items.forEach((item) => result.push({ id: item.id, columns: item, cells: { ...item } }));
        }
      });
      return result;
    });

    const rows = computed(() => {
      if (props.serverMode) return groupedRows.value;
      const start = (currentPage.value - 1) * itemsPerPage.value;
      return groupedRows.value.slice(start, start + itemsPerPage.value);
    });

    const totalPages = computed(() => {
      if (props.serverMode) return Math.max(1, Math.ceil(props.totalRows / itemsPerPage.value));
      return Math.ceil(sortedData.value.length / itemsPerPage.value);
    });

    const visiblePages = computed(() => {
      const pages = [];
      const startPage = Math.max(1, currentPage.value - 3);
      const endPage = Math.min(totalPages.value, currentPage.value + 3);
      if (startPage > 1) pages.push(1);
      if (startPage > 2) pages.push({ label: "...", direction: "backward" });
      for (let i = startPage; i <= endPage; i++) pages.push(i);
      if (endPage < totalPages.value - 1) pages.push({ label: "...", direction: "forward" });
      if (endPage < totalPages.value) pages.push(totalPages.value);
      return pages;
    });

    const allPageRowsSelected = computed(() => {
      const pageRows = rows.value.filter((r) => !r.isGroup);
      return pageRows.length > 0 && pageRows.every((row) => selectedRows.value["row-" + row.id]);
    });

    const somePageRowsSelected = computed(() => rows.value.some((row) => !row.isGroup && selectedRows.value["row-" + row.id]));

    return {
      rows,
      tableheaders,
      visibleHeaders,
      sortKey,
      sortOrders,
      sortedData,
      groupedBy,
      customComponents,
      currentPage,
      totalPages,
      visiblePages,
      selectedRows,
      onlySelected,
      editMode,
      removeCurrentOffer,
      localFilters,
      itemsPerPage,
      collapsedGroups,
      labelExportLoading,
      resyncFinancaLoading,
      showLabelModal,
      labelModalMode,
      labelNoticeCount,
      resizing,
      resizingField,
      resizeStartX,
      resizeStartWidth,
      currentSortField,
      currentSortDir,
      allPageRowsSelected,
      somePageRowsSelected,
    };
  },
};
</script>

<style scoped>
.dable-grid__scroll-bar {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-bottom: 0.35rem;
  min-height: 1.5rem;
}

.dable-scroll-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  background: #fff;
  color: #475569;
  cursor: pointer;
  padding: 0;
}

.dable-scroll-btn:hover:not(:disabled) {
  background: #f8fafc;
  border-color: #cbd5e1;
}

.dable-scroll-btn:disabled {
  opacity: 0.35;
  cursor: default;
}

.dable-scroll-hint {
  font-size: 0.7rem;
  color: #94a3b8;
  margin-right: auto;
}

.dable-grid__table-wrap {
  overflow: auto;
  flex: 1;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
}

.dable-table {
  border-collapse: collapse;
  background: #fff;
  width: max-content;
  min-width: 100%;
  table-layout: fixed;
}

.dable-table__head {
  position: sticky;
  top: 0;
  z-index: 10;
  background: #f8fafc;
}

.dable-table__header-row .dable-table__th {
  border-bottom: 1px solid #e5e7eb;
  padding: 0.45rem 0.5rem;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  color: #64748b;
  vertical-align: bottom;
  position: relative;
}

.dable-table__filter-row .dable-table__filter-cell {
  padding: 0.25rem 0.35rem;
  border-bottom: 2px solid #e2e8f0;
  background: #f1f5f9;
}

.dable-table__th-inner {
  display: flex;
  align-items: center;
  gap: 0.2rem;
  position: relative;
  min-height: 1.25rem;
}

.dable-table__drag-handle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 0.85rem;
  color: #94a3b8;
  cursor: grab;
  flex-shrink: 0;
  font-size: 0.65rem;
}

.dable-table__drag-handle:active {
  cursor: grabbing;
}
.dable-table__th-label {
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  color: inherit;
  cursor: pointer;
  text-align: left;
  flex: 1;
  min-width: 0;
}

.dable-filter-input {
  width: 100%;
  border: 1px solid #cbd5e1;
  border-radius: 4px;
  padding: 0.2rem 0.35rem;
  font-size: 0.75rem;
  background: #fff;
}

.dable-table__checkbox-col {
  width: 44px;
  min-width: 44px;
  text-align: center;
  position: sticky;
  left: 0;
  z-index: 12;
  background: #f8fafc;
  border-right: 1px solid #e2e8f0;
}

.dable-table__checkbox-col--head {
  vertical-align: middle;
  background: #f8fafc;
}

.dable-checkbox-label--header {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100%;
  padding: 0.35rem 0;
}

.dable-table__row .dable-table__checkbox-col,
.dable-table__row--inactive .dable-table__checkbox-col {
  background: #f8fafc;
}

.dable-table__row:nth-child(even) .dable-table__checkbox-col {
  background: #f1f5f9;
}

.dable-table__row:hover .dable-table__checkbox-col {
  background: #e0f2fe;
}

.dable-checkbox-label {
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  margin: 0;
  padding: 0.25rem 0;
}

.dable-checkbox {
  width: 18px;
  height: 18px;
  cursor: pointer;
  accent-color: #3b82f6;
  flex-shrink: 0;
}

.dable-table__row:nth-child(even) {
  background: #fafafa;
}

.dable-table__row:hover {
  background: #f0f9ff;
}

.dable-table__row--inactive {
  background: #fef2f2 !important;
}

.dable-table__td {
  padding: 0.4rem 0.5rem;
  font-size: 0.78rem;
  border-bottom: 1px solid #f1f5f9;
  vertical-align: middle;
}

.dable-table__cell {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dable-table__empty {
  text-align: center;
  padding: 2rem;
  color: #94a3b8;
}

.dable-table__group-row {
  background: #eff6ff;
}

.dable-pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 0.5rem;
  gap: 0.5rem;
}

.dable-pagination__pages {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
}

.dable-pagination__btn {
  border: 1px solid #d1d5db;
  background: #fff;
  border-radius: 4px;
  padding: 0.2rem 0.55rem;
  font-size: 0.75rem;
  cursor: pointer;
}

.dable-pagination__btn--active {
  background: #dbeafe;
  border-color: #3b82f6;
}

.dable-pagination__total {
  color: #94a3b8;
  font-size: 0.75rem;
}

.resizer {
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  width: 8px;
  cursor: col-resize;
  z-index: 2;
}

.resizer:hover {
  background: rgba(59, 130, 246, 0.15);
}

.is-edited {
  background-color: #fef08a;
}

.dragging {
  opacity: 0.7;
}

.hover-to-drop {
  border: 2px dashed #666;
}

:global(body.resizing) {
  cursor: ew-resize;
}
</style>
